/**
 * One-shot sounds (GDD §9): cannon, enemy tracer cracks, bridge-gun bursts, flak launches and bursts, tracer hits on
 * a bridge, explosions sized to the blast (boom, debris, splash), splashes and the extra-jet chime. Each recipe
 * re-triggers the envelopes of one persistent slot (src/audio/voices.ts) and returns how long the sound lasts.
 * Everything is synthesized (judgment call 41).
 */
import { boomShape, createBoomShape, hz, WATER_HEIGHT, type BoomShape } from './tuning.ts';
import { release, setAt, strike, sweep, type Slot } from './voices.ts';

export const SFX_KINDS = [
  'cannon',
  'crack',
  'gunBurst',
  'flakLaunch',
  'flak',
  'clank',
  'boom',
  'splash',
  'spanSplash',
  'chime',
] as const;
export type SfxKind = (typeof SFX_KINDS)[number];

const shape: BoomShape = createBoomShape();

/** ±4% so repeated sounds don't sound mechanical. */
const vary = (): number => 0.96 + Math.random() * 0.08;

/** Debris: a run of short crackles on the band-passed layer, thinning out over `secs`. */
function crackle(p: AudioParam, now: number, t: number, peak: number, secs: number): void {
  release(p, now);
  const n = Math.round(6 + 10 * peak);
  let at = t + 0.04;
  for (let i = 0; i < n && at < t + secs; i++) {
    p.setValueAtTime(peak * (1 - i / n) * (0.45 + Math.random() * 0.55), at);
    p.setTargetAtTime(0, at, 0.012);
    at += 0.025 + Math.random() * 0.085;
  }
}

/**
 * Plays `kind` on slot `s`, starting at audio time `t` (`now` is the context's current time). `size` is the
 * explosion's blast size (boom) or splash size; `y` tells an explosion over the water from one in the air.
 * Returns the sound's length in seconds.
 */
export function playSfx(kind: SfxKind, s: Slot, now: number, t: number, size: number, y: number): number {
  const v = vary();
  switch (kind) {
    case 'cannon':
      // Punchy: a falling square for the report, a low thump, a snap and a little air.
      s.osc.type = 'square';
      sweep(s.osc.frequency, now, t, 160 * v, 55, 0.05);
      strike(s.tone.gain, now, t, 0.13, 0.002, 0.07);
      setAt(s.low.frequency, now, t, 700);
      strike(s.lowEnv.gain, now, t, 0.65, 0.001, 0.08);
      setAt(s.mid.frequency, now, t, 2200 * v);
      s.mid.Q.setValueAtTime(0.8, t);
      strike(s.midEnv.gain, now, t, 0.45, 0.001, 0.04);
      setAt(s.high.frequency, now, t, 6000);
      strike(s.highEnv.gain, now, t, 0.16, 0.001, 0.02);
      return 0.09;
    case 'crack':
      // A helicopter's tracer: a sharp, high crack with a short body.
      s.osc.type = 'triangle';
      sweep(s.osc.frequency, now, t, 1300 * v, 500, 0.04);
      strike(s.tone.gain, now, t, 0.6, 0.001, 0.04);
      setAt(s.low.frequency, now, t, 400);
      strike(s.lowEnv.gain, now, t, 1.5, 0.002, 0.06);
      setAt(s.mid.frequency, now, t, 3400 * v);
      s.mid.Q.setValueAtTime(1.4, t);
      strike(s.midEnv.gain, now, t, 5.6, 0.001, 0.05);
      setAt(s.high.frequency, now, t, 5500);
      strike(s.highEnv.gain, now, t, 2.5, 0.001, 0.03);
      return 0.08;
    case 'gunBurst':
      // A bridge gun: heavier and lower than a helicopter's.
      s.osc.type = 'square';
      sweep(s.osc.frequency, now, t, 220 * v, 70, 0.06);
      strike(s.tone.gain, now, t, 0.5, 0.001, 0.07);
      setAt(s.low.frequency, now, t, 500);
      strike(s.lowEnv.gain, now, t, 5, 0.001, 0.1);
      setAt(s.mid.frequency, now, t, 1500 * v);
      s.mid.Q.setValueAtTime(1, t);
      strike(s.midEnv.gain, now, t, 5, 0.001, 0.07);
      setAt(s.high.frequency, now, t, 4000);
      strike(s.highEnv.gain, now, t, 1.6, 0.001, 0.03);
      return 0.12;
    case 'flakLaunch':
      // A gunboat's flak gun: a deep thoomp.
      s.osc.type = 'sine';
      sweep(s.osc.frequency, now, t, 140 * v, 50, 0.12);
      strike(s.tone.gain, now, t, 1.3, 0.003, 0.16);
      setAt(s.low.frequency, now, t, 260);
      strike(s.lowEnv.gain, now, t, 1.3, 0.002, 0.18);
      setAt(s.mid.frequency, now, t, 700);
      s.mid.Q.setValueAtTime(0.8, t);
      strike(s.midEnv.gain, now, t, 0.4, 0.002, 0.06);
      release(s.highEnv.gain, now);
      return 0.22;
    case 'flak':
      // A flak shell bursting: a thump with a crack on top.
      s.osc.type = 'sine';
      sweep(s.osc.frequency, now, t, 95 * v, 38, 0.3);
      strike(s.tone.gain, now, t, 1, 0.002, 0.35);
      setAt(s.low.frequency, now, t, 520);
      strike(s.lowEnv.gain, now, t, 1.1, 0.002, 0.4);
      setAt(s.mid.frequency, now, t, 1100);
      s.mid.Q.setValueAtTime(0.7, t);
      strike(s.midEnv.gain, now, t, 0.6, 0.001, 0.12);
      setAt(s.high.frequency, now, t, 3500);
      strike(s.highEnv.gain, now, t, 0.3, 0.001, 0.05);
      return 0.45;
    case 'clank':
      // A tracer striking the bridge: a short metallic ring.
      s.osc.type = 'square';
      sweep(s.osc.frequency, now, t, 1450 * v, 1150 * v, 0.05);
      strike(s.tone.gain, now, t, 0.25, 0.001, 0.06);
      release(s.lowEnv.gain, now);
      setAt(s.mid.frequency, now, t, 2600 * v);
      s.mid.Q.setValueAtTime(8, t);
      strike(s.midEnv.gain, now, t, 4, 0.001, 0.12);
      setAt(s.high.frequency, now, t, 4500);
      strike(s.highEnv.gain, now, t, 1.8, 0.001, 0.025);
      return 0.15;
    case 'boom': {
      // Sized to the blast: a falling sine for the low end, a rumble of noise, debris, and a splash over water.
      const b = boomShape(size, y < WATER_HEIGHT, shape);
      s.osc.type = 'sine';
      sweep(s.osc.frequency, now, t, b.startHz * v, b.endHz, b.secs * 0.5);
      strike(s.tone.gain, now, t, b.gain * 1.2, 0.004, b.secs * 0.7);
      sweep(s.low.frequency, now, t, b.bodyHz, 120, b.secs);
      strike(s.lowEnv.gain, now, t, b.gain * 1.3, 0.003, b.secs);
      setAt(s.mid.frequency, now, t, 1300);
      s.mid.Q.setValueAtTime(0.9, t);
      crackle(s.midEnv.gain, now, t, b.debris, b.secs * 0.6);
      if (b.splash > 0) {
        setAt(s.high.frequency, now, t, 900);
        strike(s.highEnv.gain, now, t + 0.06, b.splash, 0.06, b.secs * 0.7);
      } else release(s.highEnv.gain, now);
      return b.secs + 0.15;
    }
    case 'splash': {
      // A helicopter hitting the water.
      const k = size / 1.2;
      release(s.tone.gain, now);
      setAt(s.low.frequency, now, t, 220);
      strike(s.lowEnv.gain, now, t, 0.4 * k, 0.01, 0.35);
      setAt(s.mid.frequency, now, t, 500);
      s.mid.Q.setValueAtTime(0.6, t);
      strike(s.midEnv.gain, now, t, 0.35 * k, 0.02, 0.45);
      setAt(s.high.frequency, now, t, 1000);
      strike(s.highEnv.gain, now, t, 0.5 * k, 0.03, 0.7);
      return 0.9;
    }
    case 'spanSplash':
      // A bridge span falling into the river.
      s.osc.type = 'sine';
      sweep(s.osc.frequency, now, t, 70, 35, 0.4);
      strike(s.tone.gain, now, t, 0.4, 0.01, 0.5);
      setAt(s.low.frequency, now, t, 180);
      strike(s.lowEnv.gain, now, t, 0.7, 0.02, 0.8);
      setAt(s.mid.frequency, now, t, 400);
      s.mid.Q.setValueAtTime(0.5, t);
      strike(s.midEnv.gain, now, t, 0.5, 0.04, 1);
      setAt(s.high.frequency, now, t, 700);
      strike(s.highEnv.gain, now, t, 0.8, 0.05, 1.6);
      return 1.8;
    case 'chime': {
      // An extra jet: three rising notes (A5, D6, A6).
      s.osc.type = 'triangle';
      const f = s.osc.frequency;
      const g = s.tone.gain;
      f.cancelScheduledValues(now);
      release(g, now);
      for (let i = 0; i < 3; i++) {
        const at = t + i * 0.09;
        f.setValueAtTime(hz(i === 0 ? 81 : i === 1 ? 86 : 93), at);
        g.setValueAtTime(0, at);
        g.linearRampToValueAtTime(0.16, at + 0.01);
        g.setTargetAtTime(0, at + 0.01, 0.09);
      }
      release(s.lowEnv.gain, now);
      release(s.midEnv.gain, now);
      release(s.highEnv.gain, now);
      return 0.8;
    }
  }
}
